// CommandPalette - real Cmd/Ctrl-K search over pages + live data.
//
// G360_K461 - this organisation's records. Tools, policies, risks and
// incidents were read with no tenant, so a member of two organisations (and
// staff) searched both and opened the other one's record into this one's
// page. They are now read for the organisation on screen, and the cache is
// kept per organisation.
// Mounted once in ConsoleApp (which provides navigate). Opens on Cmd/Ctrl-K
// or when the top search bar is clicked (listens for a custom 'g360-open-search' event).
//
// Searches:
//   - Pages (the nav routes) -> navigate by hash
//   - AI tools, policies, risks, incidents (live, RLS-scoped) -> navigate to the page
//
// Note: navigation is per-PAGE (the app routes by hash, not per-record), so a
// result opens the right page. Where a page reads ?q=, we also pass the term.

import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, CornerDownLeft, Hash, Radar, SlidersHorizontal, AlertTriangle, Flag } from 'lucide-react';
import { useAuth, supabase } from '@govern360/shared';
import { listPolicies } from '../lib/policyApi.js';
import { listIncidents } from '../lib/grcApi.js';
// G360_K641
import { lockBodyScroll } from '@govern360/shared/components/Drawer.jsx';

// One table for one organisation, the first 1,000 rows - a search box, not
// a report.
async function readFor(table, tenantId, order) {
  const { data, error } = await supabase.from(table).select('*')
    .eq('tenant_id', tenantId).order(order, { ascending: false }).limit(1000);
  if (error) throw error;
  return data || [];
}

// G360_K715 - THE PAGES ARE THE MENU. They used to be a hand-kept list of 25
// names that had fallen behind the sidebar: Connections, Governance Register,
// Token Governance, Endpoint Governance and thirty more could not be found.
// They now come from the sidebar itself (navGroups from ConsoleApp, already
// filtered to what this role may open), so a page added or renamed in the
// menu is found the same day. Matching is per word, ignores case and word
// endings (connector finds Connections), and reads what each page is for
// (SYN below), so a page is found by what it does as well as by its name.
const EXTRA_PAGES = [
  { id: 'documentation', label: 'Documentation', route: 'documentation', group: 'Help' },
  { id: 'faq', label: 'FAQ', route: 'faq', group: 'Help' },
];
const SYN = {
  zerotrust: 'zero trust ztmm cisa pillars maturity',
  execboard: 'board executive summary directors',
  execcaio: 'caio chief ai officer executive',
  execcio: 'cio chief information officer executive',
  execciso: 'ciso chief information security officer security executive',
  execcfo: 'cfo spend cost finance budget executive',
  execcfoval: 'cfo value roi return benefit finance executive',
  execcro: 'cro chief risk officer executive',
  exposurescore: 'exposure score posture rating',
  starthere: 'decisions start here queue next actions todo',
  dashboard: 'home overview summary',
  ainews: 'news articles',
  inventory: 'inventory tools apps shadow catalog devices endpoints laptops computers',
  discovery: 'discovery discover scan scans candidates shadow triage',
  agents: 'agents models copilot studio agent builder llm foundation registry all agents built agents',
  nhi: 'nhi non human identity identities service principals credentials secrets app registrations',
  exposurepaths: 'exposure paths attack path trifecta exfiltration',
  govregister: 'governance register decisions accept defer dismiss register',
  releasecert: 'release certification approve approval certify',
  risks: 'risk register risks registry findings',
  owasp: 'owasp top 10 llm asi agentic',
  myagents: 'my agents your agents owned',
  behrisk: 'behavioral behaviour behavior anomaly anomalies',
  dataposture: 'data posture purview labels sensitivity dlp sharepoint',
  vendors: 'vendor vendors third party supplier suppliers',
  tokengov: 'token tokens governance spend cost budget budgets usage keys api keys openai anthropic ramp concur',
  copilotconsum: 'copilot consumption credits seats licences licenses m365 usage',
  purposereg: 'purpose register purposes sign attest attestation',
  archreview: 'architecture review design review assessment',
  supervision: 'supervision health human in the loop oversight',
  accessreq: 'access requests request approvals',
  routingrules: 'approval routing rules approvers',
  incidents: 'incidents incident response cases',
  policies: 'policies policy rules prevention dlp prompt',
  datashield: 'data shield dlp redact redaction sensitive',
  mcpgw: 'mcp gateway model context protocol servers tools',
  netblock: 'network block list firewall palo alto prisma edl',
  purview: 'purview microsoft dlp labels',
  deploy: 'endpoint governance deploy guard extension browser prompt guard devices endpoints intune install inventory collector deploy policies',
  coverage: 'enforcement coverage planes matrix',
  compliance: 'compliance enforcement eu ai act frameworks controls iso nist soc',
  reports: 'reports report export pdf',
  audit: 'audit log logs history events trail',
  integrations: 'connector connectors integration apps marketplace webhook slack teams jira',
  connections: 'connector connectors connect integration source sources data sources microsoft entra azure aws google okta github salesforce servicenow sync',
  siem: 'siem streaming splunk sentinel webhook export events',
  users: 'users roles team members people invite permissions',
  security: 'security settings sso mfa session password',
  govrules: 'alert rules alerts notifications review rules',
  g360copilot: 'g360 copilot assistant chat',
  branding: 'branding logo colours colors theme',
  billing: 'billing invoice plan subscription payment',
  implguide: 'implementation guide setup onboarding',
  adminguide: 'administrator guide admin guide manual documentation',
  help: 'help support contact ticket',
  account: 'my account profile password settings',
  documentation: 'documentation docs manual guide',
  faq: 'faq questions answers',
};
function words(s) { return String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean); }
function stem(w) {
  let s = String(w || '').toLowerCase();
  const suf = ['ations', 'ation', 'ions', 'ion', 'ings', 'ing', 'ors', 'or', 'ers', 'er', 'ies', 'es', 's'];
  for (const x of suf) { if (s.length - x.length >= 4 && s.endsWith(x)) { s = s.slice(0, -x.length); break; } }
  return s;
}
function buildPages(navGroups) {
  const out = []; const seen = {};
  (navGroups || []).forEach((g) => (g.items || []).forEach((it) => {
    if (!it || !it.route || seen[it.route]) return;
    seen[it.route] = 1;
    const hay = [it.label, g.label, it.route, (it.also || []).join(' '), SYN[it.id] || ''].join(' ').toLowerCase();
    out.push({ id: it.id, label: it.label, route: it.route, group: g.label, hay, stems: words(hay).map(stem) });
  }));
  EXTRA_PAGES.forEach((p) => {
    if (seen[p.route]) return;
    const hay = [p.label, p.group, p.route, SYN[p.id] || ''].join(' ').toLowerCase();
    out.push({ ...p, hay, stems: words(hay).map(stem) });
  });
  return out;
}
function matchPages(pages, needle) {
  const n = String(needle || '').trim().toLowerCase();
  const terms = words(n);
  if (!terms.length) return [];
  const out = [];
  for (const p of pages) {
    const ok = terms.every((t) => {
      if (p.hay.indexOf(t) >= 0) return true;
      const ts = stem(t);
      return p.stems.some((w) => w.startsWith(ts) || (w.length >= 4 && ts.startsWith(w)));
    });
    if (!ok) continue;
    const lab = p.label.toLowerCase();
    const labStems = words(p.label).map(stem);
    let score = 4;
    if (terms.every((t) => labStems.some((w) => w.startsWith(stem(t))))) score = 3;
    if (lab.indexOf(n) >= 0) score = 2;
    if (lab.startsWith(n)) score = 1;
    out.push({ label: p.label, route: p.route, group: p.group, score });
  }
  out.sort((a, b) => a.score - b.score || a.label.localeCompare(b.label));
  return out.slice(0, 8);
}

export default function CommandPalette({ navigate, navGroups }) {
  const { tenant } = useAuth();
  const tid = (tenant && tenant.id) || null;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ pages: [], tools: [], policies: [], risks: [], incidents: [] });
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  // G360_K468 - which data sources could not be searched this time.
  const [unsearched, setUnsearched] = useState([]);
  const inputRef = useRef(null);
  const cache = useRef(null); // {tools, policies, risks, incidents}
  // G360_K715 - the menu as searchable pages, rebuilt when the menu changes.
  const pagesRef = useRef([]);
  pagesRef.current = buildPages(navGroups);

  // Open on Cmd/Ctrl-K, on the custom event, AND when the top search bar
  // (.ap-search, rendered by the shared AppShell) is clicked. Using event
  // delegation means we never have to edit the shared AppShell component.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    };
    const onOpen = () => setOpen(true);
    const onDocClick = (e) => { if (e.target.closest && e.target.closest('.ap-search')) { e.preventDefault(); setOpen(true); } };
    window.addEventListener('keydown', onKey);
    window.addEventListener('g360-open-search', onOpen);
    document.addEventListener('click', onDocClick, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('g360-open-search', onOpen);
      document.removeEventListener('click', onDocClick, true);
    };
  }, []);

  // Load live data once when first opened.
  useEffect(() => {
    if (!open || !tid || (cache.current && cache.current.tid === tid)) return;
    setLoading(true);
    /* G360_K468 - a source that could not be read used to become an empty
       list, cached for the session: "No matches" when the truth was "not
       searched". The failed sources are named under the results, and the
       cache is not kept, so opening the palette again retries them. */
    const failed = [];
    const miss = (label) => () => { failed.push(label); return []; };
    const dataOf = (label) => (r) => { if (r && r.error) { failed.push(label); return []; } return (r && r.data) || []; };
    Promise.all([
      readFor('ai_tools', tid, 'user_count').catch(miss('AI tools')),
      listPolicies(tid).then(dataOf('policies')).catch(miss('policies')),
      readFor('risks', tid, 'created_at').catch(miss('risks')),
      listIncidents(tid).then(dataOf('incidents')).catch(miss('incidents')),
    ]).then(([tools, policies, risks, incidents]) => {
      cache.current = { tid: failed.length ? null : tid, tools: tools || [], policies: policies || [], risks: risks || [], incidents: incidents || [] };
      setUnsearched(failed);
      setLoading(false);
      compute(q);
    });
  }, [open, tid]); // eslint-disable-line

  const compute = useCallback((term) => {
    const t = term.trim().toLowerCase();
    const c = cache.current || { tools: [], policies: [], risks: [], incidents: [] };
    const m = (s) => s && s.toLowerCase().includes(t);
    const pages = t ? matchPages(pagesRef.current, t) : []; // G360_K715
    if (!t) { setResults({ pages, tools: [], policies: [], risks: [], incidents: [] }); setActive(0); return; }
    setResults({
      pages,
      tools: c.tools.filter((x) => m(x.name) || m(x.vendor)).slice(0, 5),
      policies: c.policies.filter((x) => m(x.name) || m(x.description)).slice(0, 5),
      risks: c.risks.filter((x) => m(x.title)).slice(0, 5),
      incidents: c.incidents.filter((x) => m(x.title)).slice(0, 5),
    });
    setActive(0);
  }, []);

  useEffect(() => { if (open) compute(q); }, [q, open, compute]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

  // Lock the background from scrolling while the palette is open, so it stays put.
  useEffect(() => {
    // G360_K641 - the same counted lock as the drawers, so closing in any
    // order always gives the page its scroll back.
    if (open) return lockBodyScroll();
  }, [open]);

  // Flatten results into an ordered list for keyboard nav.
  const flat = [
    ...results.pages.map((p) => ({ kind: 'page', label: p.label, sub: p.group, route: p.route, icon: Hash })),
    ...results.tools.map((x) => ({ kind: 'tool', label: x.name, sub: x.vendor, route: 'inventory', icon: Radar })),
    ...results.policies.map((x) => ({ kind: 'policy', label: x.name, route: 'policies', icon: SlidersHorizontal })),
    ...results.risks.map((x) => ({ kind: 'risk', label: x.title, sub: x.severity, route: 'risks', icon: AlertTriangle })),
    ...results.incidents.map((x) => ({ kind: 'incident', label: x.title, sub: x.severity, route: 'incidents', icon: Flag })),
  ];

  const choose = (item) => {
    if (!item) return;
    setOpen(false); setQ('');
    if (navigate) navigate(item.route); else window.location.hash = `#${item.route}`;
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(flat[active]); }
  };

  if (!open) return null;

  const SECTIONS = [
    ['Pages', results.pages.map((p) => ({ kind: 'page', label: p.label, sub: p.group, route: p.route, icon: Hash }))],
    ['AI tools', results.tools.map((x) => ({ kind: 'tool', label: x.name, sub: x.vendor, route: 'inventory', icon: Radar }))],
    ['Policies', results.policies.map((x) => ({ kind: 'policy', label: x.name, route: 'policies', icon: SlidersHorizontal }))],
    ['Risks', results.risks.map((x) => ({ kind: 'risk', label: x.title, sub: x.severity, route: 'risks', icon: AlertTriangle }))],
    ['Incidents', results.incidents.map((x) => ({ kind: 'incident', label: x.title, sub: x.severity, route: 'incidents', icon: Flag }))],
  ].filter(([, items]) => items.length);

  let idx = -1; // running index to match `flat` order for highlight

  return (
    <div onClick={() => setOpen(false)}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '10vh', overflow: 'hidden' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(640px, 92vw)', background: 'var(--g-surface)', borderRadius: 'var(--g-r-lg)', boxShadow: 'var(--g-e3)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--g-divider)' }}>
          <Search size={18} style={{ color: 'var(--g-text-2)' }} />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Search pages, tools, policies, risks, incidents..."
            style={{ flex: 1, border: 0, outline: 'none', fontSize: 15, background: 'transparent', color: 'var(--g-text)' }} />
          <kbd style={{ fontSize: 11, color: 'var(--g-text-2)', border: '1px solid var(--g-border)', borderRadius: 4, padding: '2px 6px' }}>Esc</kbd>
        </div>

        <div style={{ maxHeight: '52vh', overflowY: 'auto', padding: 8 }}>
          {loading && <div style={{ padding: 16, color: 'var(--g-text-2)', fontSize: 13 }}>Loading...</div>}
          {!loading && flat.length === 0 && (
            <div style={{ padding: 16, color: 'var(--g-text-2)', fontSize: 13 }}>
              {q ? `No matches for "${q}".` : 'Type to search pages and your data.'}
            </div>
          )}
          {!loading && unsearched.length > 0 && (
            <div style={{ padding: '6px 10px', color: 'var(--g-warning)', fontSize: 12 }}>
              Not searched - could not be read: {unsearched.join(', ')}.
            </div>
          )}
          {SECTIONS.map(([title, items]) => (
            <div key={title} style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--g-text-3)', textTransform: 'uppercase', letterSpacing: '.05em', padding: '6px 10px' }}>{title}</div>
              {items.map((item) => {
                idx += 1; const i = idx; const Icon = item.icon;
                return (
                  <button key={`${item.kind}-${item.label}-${i}`} onMouseEnter={() => setActive(i)} onClick={() => choose(item)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', border: 0, cursor: 'pointer',
                      background: active === i ? 'var(--g-primary-50)' : 'transparent', borderRadius: 8, padding: '9px 10px',
                      color: active === i ? 'var(--g-primary-700)' : 'var(--g-text)' }}>
                    <Icon size={16} style={{ color: 'var(--g-text-2)', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 14 }}>{item.label}{item.sub ? <span style={{ color: 'var(--g-text-2)', fontSize: 12 }}> &middot; {item.sub}</span> : null}</span>
                    {active === i && <CornerDownLeft size={14} style={{ color: 'var(--g-text-2)' }} />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
